import { useState, useCallback, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { ErrorMessage } from '@hookform/error-message'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import DashboardWrapper from '../../Dashboard/components/DashboardWrapper'
import Toast from '../../../components/Toast/Toast'
import { TAGS } from '../../../services/Tags'
import {
  createFormType,
  getFormTypesList,
  updateFormType,
  deleteFormType,
} from '../../../services/Forms'

// ─── Constants ────────────────────────────────────────────────────────────────

const accent = '#01A76A'
const accentLight = 'rgba(1,167,106,0.08)'

// ─── Helpers ──────────────────────────────────────────────────────────────────

const SkeletonCard = () => (
  <div
    style={{
      borderRadius: 12,
      padding: '16px 18px',
      border: '1.5px solid #eef0f3',
      background: '#fafafa',
    }}
  >
    <style>{`@keyframes shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}`}</style>
    {[{ w: '35%', h: 14 }, { w: '95%', h: 11 }, { w: '60%', h: 11 }].map((s, i) => (
      <div
        key={i}
        style={{
          height: s.h,
          width: s.w,
          borderRadius: 6,
          background: 'linear-gradient(90deg,#e8ecf0 25%,#f5f6f8 50%,#e8ecf0 75%)',
          backgroundSize: '200% 100%',
          animation: 'shimmer 1.4s infinite',
          marginBottom: i < 2 ? 8 : 0,
        }}
      />
    ))}
  </div>
)

// ─── Form panel ───────────────────────────────────────────────────────────────

const FormPanel = ({ editingType, onCancel, onCreate, onUpdate, isCreating, isUpdating, panelHeight }) => {
  const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm({
    defaultValues: { name: '', description: '', active: true },
  })

  const isEditing = Boolean(editingType)

  useEffect(() => {
    if (editingType) {
      setValue('name', editingType.name ?? '')
      setValue('description', editingType.description ?? '')
      setValue('active', editingType.active ?? true)
    } else {
      reset()
    }
  }, [editingType, setValue, reset])

  const handleCancel = () => { reset(); onCancel() }

  const onSubmit = (data) => {
    if (isEditing) {
      onUpdate({
        id: editingType.id,
        name: data.name,
        description: data.description,
        active: data.active,
      })
    } else {
      onCreate({ name: data.name, description: data.description })
      reset()
    }
  }

  const inputBase = {
    borderRadius: 8,
    fontSize: 13,
    border: '1.5px solid #e0e4ea',
    padding: '9px 13px',
    width: '100%',
    outline: 'none',
    background: '#fff',
    color: '#222',
    lineHeight: 1.5,
    boxSizing: 'border-box',
    transition: 'border-color 0.15s',
  }

  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 14,
        border: isEditing ? `2px solid ${accent}` : '1.5px solid #e8ecf0',
        boxShadow: isEditing ? `0 0 0 4px ${accentLight}` : '0 2px 10px rgba(0,0,0,0.05)',
        display: 'flex',
        flexDirection: 'column',
        height: panelHeight,
        transition: 'border-color 0.2s, box-shadow 0.2s',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          padding: '18px 22px 14px',
          borderBottom: '1px solid #f0f2f5',
          flexShrink: 0,
        }}
      >
        <div className="d-flex align-items-center gap-2">
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: 9,
              background: isEditing ? accentLight : '#f3f4f6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 16,
              flexShrink: 0,
            }}
          >
            {isEditing ? '✏️' : '➕'}
          </div>
          <div>
            <h6 style={{ margin: 0, fontWeight: 700, fontSize: 14, color: '#1a1a2e', lineHeight: 1.3 }}>
              {isEditing ? 'Editar tipo de formulario' : 'Nuevo tipo de formulario'}
            </h6>
            <span style={{ fontSize: 11, color: isEditing ? accent : '#aaa', fontWeight: 600 }}>
              {isEditing ? 'Modo edición activo' : 'Completa los campos y guarda'}
            </span>
          </div>
        </div>
      </div>

      <form
        onSubmit={handleSubmit(onSubmit)}
        style={{ flex: 1, display: 'flex', flexDirection: 'column', overflowY: 'auto', padding: '18px 22px' }}
      >
        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#555', display: 'block', marginBottom: 5 }}>
            Nombre <span style={{ color: '#e53935' }}>*</span>
          </label>
          <input
            style={inputBase}
            placeholder="Ej: Datos fiscales y de facturación"
            {...register('name', { required: 'El nombre es obligatorio' })}
          />
          <ErrorMessage
            errors={errors}
            name="name"
            render={({ message }) => (
              <small style={{ color: '#e53935', fontSize: 11, marginTop: 4, display: 'block' }}>{message}</small>
            )}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, marginBottom: 16 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#555', display: 'block', marginBottom: 5 }}>
            Descripción
          </label>
          <textarea
            style={{ ...inputBase, flex: 1, resize: 'none', minHeight: 100 }}
            placeholder="Describe brevemente este tipo de formulario..."
            {...register('description')}
          />
        </div>

        {isEditing && (
          <div style={{ marginBottom: 16 }}>
            <label className="d-flex align-items-center gap-2" style={{ fontSize: 12, fontWeight: 600, color: '#555' }}>
              <input type="checkbox" {...register('active')} />
              Activo
            </label>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flexShrink: 0 }}>
          <button
            type="submit"
            disabled={isCreating || isUpdating}
            style={{
              background: isCreating || isUpdating ? '#b2dfdb' : accent,
              color: '#fff',
              border: 'none',
              borderRadius: 9,
              padding: '10px 0',
              fontSize: 13,
              fontWeight: 700,
              cursor: isCreating || isUpdating ? 'not-allowed' : 'pointer',
              width: '100%',
              transition: 'background 0.2s',
              letterSpacing: '0.01em',
            }}
          >
            {isCreating || isUpdating ? 'Guardando...' : isEditing ? '✓ Actualizar tipo' : '+ Agregar tipo'}
          </button>
          {isEditing && (
            <button
              type="button"
              onClick={handleCancel}
              style={{
                background: '#f3f4f6',
                color: '#666',
                border: 'none',
                borderRadius: 9,
                padding: '10px 0',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                width: '100%',
              }}
            >
              Cancelar edición
            </button>
          )}
        </div>
      </form>
    </div>
  )
}

// ─── Type card ────────────────────────────────────────────────────────────────

const TypeCard = ({ type, isEditing, onEdit, onDelete, isDeleting }) => (
  <div
    style={{
      borderRadius: 12,
      padding: '14px 16px',
      border: isEditing ? `1.5px solid ${accent}` : '1.5px solid #eaecf0',
      background: isEditing ? accentLight : '#fff',
      transition: 'border-color 0.2s, background 0.2s, box-shadow 0.2s',
      boxShadow: isEditing ? `0 0 0 3px ${accentLight}` : '0 1px 4px rgba(0,0,0,0.04)',
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
    }}
  >
    <div className="d-flex justify-content-between align-items-start gap-2">
      <div style={{ minWidth: 0 }}>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <span style={{ fontWeight: 700, fontSize: 13, color: '#1a1a2e' }}>{type.name}</span>
          {type.active === false && (
            <span
              style={{
                background: 'rgba(0,0,0,0.04)',
                color: '#888',
                borderRadius: 20,
                padding: '2px 9px',
                fontSize: 10,
                fontWeight: 700,
              }}
            >
              Inactivo
            </span>
          )}
        </div>
        <span style={{ fontSize: 11, color: '#9aa' }}>id: {type.id}</span>
      </div>
      <div className="d-flex gap-1 flex-shrink-0">
        <button
          onClick={() => onEdit(type)}
          style={{
            background: isEditing ? accent : '#f5f6f8',
            border: `1px solid ${isEditing ? accent : '#dde1e7'}`,
            borderRadius: 7,
            padding: '5px 13px',
            fontSize: 11,
            fontWeight: 700,
            cursor: 'pointer',
            color: isEditing ? '#fff' : '#444',
            whiteSpace: 'nowrap',
          }}
        >
          {isEditing ? '✓ Editando' : 'Editar'}
        </button>
        <button
          onClick={() => onDelete(type.id)}
          disabled={isDeleting}
          style={{
            background: '#fff8f8',
            border: '1px solid #ffcdd2',
            borderRadius: 7,
            padding: '5px 13px',
            fontSize: 11,
            fontWeight: 700,
            cursor: isDeleting ? 'not-allowed' : 'pointer',
            color: '#e53935',
            whiteSpace: 'nowrap',
          }}
        >
          Eliminar
        </button>
      </div>
    </div>
    {type.description && (
      <p style={{ fontSize: 12, color: '#556', margin: 0, lineHeight: 1.6 }}>{type.description}</p>
    )}
  </div>
)

// ─── Main page ────────────────────────────────────────────────────────────────

const PANEL_HEIGHT = 'calc(100vh - 220px)'

const pageStyles = `
  .form-types-wrapper.dashboard-wrapper {
    margin-left: 24px !important;
    margin-right: 24px !important;
    min-width: 0;
  }
`

const FormTypesPage = () => {
  const queryClient = useQueryClient()
  const [editingType, setEditingType] = useState(null)
  const [search, setSearch] = useState('')

  const { data: typesData, isLoading } = useQuery(
    [TAGS.form_types],
    getFormTypesList
  )

  const rawTypes = Array.isArray(typesData)
    ? typesData
    : typesData?.res?.list ?? typesData?.res ?? typesData?.list ?? []

  const types = search.trim()
    ? rawTypes.filter(
        (item) =>
          item.name?.toLowerCase().includes(search.toLowerCase()) ||
          item.description?.toLowerCase().includes(search.toLowerCase())
      )
    : rawTypes

  const invalidate = () => queryClient.invalidateQueries([TAGS.form_types])

  const { mutate: createType, isLoading: isCreating } = useMutation(createFormType, {
    onSuccess: () => { invalidate(); Toast({ title: 'Tipo de formulario creado correctamente' }) },
  })

  const { mutate: updateType, isLoading: isUpdating } = useMutation(updateFormType, {
    onSuccess: () => {
      invalidate()
      setEditingType(null)
      Toast({ title: 'Tipo de formulario actualizado correctamente' })
    },
  })

  const { mutate: deleteType, isLoading: isDeleting } = useMutation(deleteFormType, {
    onSuccess: () => { invalidate(); Toast({ title: 'Tipo de formulario eliminado', icon: 'info' }) },
  })

  const startEdit = useCallback((type) => setEditingType(type), [])
  const cancelEdit = useCallback(() => setEditingType(null), [])

  return (
    <>
      <style>{pageStyles}</style>
      <DashboardWrapper dashboardWrapperClassName="flex-grow-1 form-types-wrapper">
        <div
          className="pt-5 mt-5"
          style={{
            background: 'linear-gradient(135deg, #01A76A 0%, #019960 100%)',
            borderRadius: 14,
            padding: '24px 32px',
            marginBottom: 20,
            color: '#fff',
          }}
        >
          <h4 style={{ fontWeight: 700, margin: 0, fontSize: 20 }}>Tipos de formularios</h4>
          <p style={{ margin: '6px 0 0', opacity: 0.85, fontSize: 14 }}>
            Gestiona los tipos de formulario disponibles para el módulo de guardado de borradores.
          </p>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 2fr',
            gap: 20,
            alignItems: 'start',
            width: '100%',
          }}
        >
          <FormPanel
            editingType={editingType}
            onCancel={cancelEdit}
            onCreate={createType}
            onUpdate={updateType}
            isCreating={isCreating}
            isUpdating={isUpdating}
            panelHeight={PANEL_HEIGHT}
          />

          <div
            style={{
              background: '#fff',
              borderRadius: 14,
              border: '1.5px solid #e8ecf0',
              boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
              display: 'flex',
              flexDirection: 'column',
              height: PANEL_HEIGHT,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                padding: '18px 22px 14px',
                borderBottom: '1px solid #f0f2f5',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
              }}
            >
              <div className="d-flex align-items-center gap-2">
                <h6 style={{ margin: 0, fontWeight: 700, fontSize: 14, color: '#1a1a2e' }}>
                  Tipos
                </h6>
                {!isLoading && (
                  <span
                    style={{
                      background: accentLight,
                      color: accent,
                      borderRadius: 20,
                      padding: '2px 10px',
                      fontSize: 11,
                      fontWeight: 700,
                    }}
                  >
                    {types.length}
                  </span>
                )}
              </div>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar tipo..."
                style={{
                  borderRadius: 8,
                  border: '1.5px solid #e0e4ea',
                  padding: '7px 13px',
                  fontSize: 12,
                  outline: 'none',
                  width: 220,
                  color: '#333',
                  background: '#fafbfc',
                }}
              />
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 22px' }}>
              {isLoading ? (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                    gap: 14,
                  }}
                >
                  {[1, 2, 3, 4].map((k) => <SkeletonCard key={k} />)}
                </div>
              ) : types.length === 0 ? (
                <div
                  style={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 10,
                    color: '#aaa',
                    paddingTop: 40,
                  }}
                >
                  <span style={{ fontSize: 44 }}>📋</span>
                  <p style={{ margin: 0, fontSize: 13, fontWeight: 500 }}>
                    {search ? 'Sin resultados para esa búsqueda.' : 'No hay tipos de formulario creados aún.'}
                  </p>
                  {search && (
                    <button
                      onClick={() => setSearch('')}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: accent,
                        fontSize: 12,
                        fontWeight: 700,
                        cursor: 'pointer',
                        padding: 0,
                      }}
                    >
                      Limpiar búsqueda
                    </button>
                  )}
                </div>
              ) : (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                    gap: 14,
                    alignContent: 'start',
                  }}
                >
                  {types.map((type) => (
                    <TypeCard
                      key={type.id}
                      type={type}
                      isEditing={editingType?.id === type.id}
                      onEdit={startEdit}
                      onDelete={deleteType}
                      isDeleting={isDeleting}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </DashboardWrapper>
    </>
  )
}

export default FormTypesPage
